/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable jsdoc/require-jsdoc */
import { ReactElement, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  ActionData,
  DataRateAtrribute,
  ActionsTypes,
  DeviceAttributes
} from '../constants/telosair-interfaces';
import { useData } from '../context/data.context';
import DropdownCustom from '../controls/DropdownCustom';
import TanstackTable from './TanstackTable';
import { Filters } from '../services/localstorage.service';
import TruncatedText from './truncatedText';

type DevicesTableComponentProps = {
  devices: DeviceAttributes[];
  isActivatingDevice?: boolean;
  handleAction: (data: ActionData<DeviceAttributes>) => void;
};

type FormattedDataAttr = DeviceAttributes & {
  dataRate: DataRateAtrribute | null;
  action: null;
};

/**
 * A Devices Table Component.
 * @param props - Devices Table Component Props.
 * @returns - A React Component.
 */
export default function DevicesTable(props: DevicesTableComponentProps): ReactElement {
  const { devices, isActivatingDevice, handleAction } = props;
  const { dataRatePerDevice } = useData();
  const [formattedData, setFormattedData] = useState<FormattedDataAttr[]>([]);

  useEffect(() => {
    const tempFormattedData: FormattedDataAttr[] = devices.map((device) => {
      const tags = device.tags ? device.tags.filter((item) => item) : [];
      const dataRate = dataRatePerDevice && dataRatePerDevice[device.serialNumber];
      return {
        ...device,
        tags,
        dataRate,
        action: null
      };
    });
    setFormattedData(tempFormattedData);
  }, [devices, dataRatePerDevice]);

  const UserTableColumns = useMemo(
    () => [
      {
        id: 'deviceName',
        accessorKey: 'deviceName',
        header: 'Device Name',
        cell: (info: any) => {
          const rowData = info.row.original;
          const tags = rowData.tags ? rowData.tags.filter((item: any) => item) : [];
          return (
            <div>
              <div className="inline-flex items-center gap-x-1.5">
                <span className="material-icons-outlined text-md mr-1 text-gray-400">memory</span>
                <TruncatedText className="text-gray-300 text-lg" text={info.getValue()} />
                <span className="text-gray-500 text-xs">#{rowData.serialNumber}</span>
              </div>
              {!isActivatingDevice && (
                <div className="justify-start items-center gap-2 flex">
                  {tags.slice(0, 2).map((tag: string) => (
                    <div
                      key={tag}
                      className="px-2 py-0.5 dark:bg-gray-800 rounded justify-start items-center text-primary-100 text-xs"
                    >
                      {tag}
                    </div>
                  ))}
                  {tags.length > 2 && (
                    <div
                      key="count"
                      className="px-2 py-0.5 dark:bg-gray-800 rounded justify-start items-center text-primary-100 text-xs"
                    >
                      +{tags.length - 2}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        }
      },
      {
        id: 'type',
        accessorKey: 'deviceType',
        header: 'Device Type',
        cell: (info: any) => {
          return (
            <span className="capitalize text-gray-300">
              {info.getValue()?.toLowerCase() || '-'}
            </span>
          );
        }
      },
      {
        id: 'dataRate',
        accessorKey: 'dataRate.value',
        header: 'Data rate',
        cell: (info: any) => {
          const rowData = info.row.original;
          return (
            <div>
              <div
                className={clsx({
                  'text-success-500': rowData.dataRate?.state === 'working',
                  'text-warning-500': rowData.dataRate?.state === 'moderate',
                  'text-error-500': rowData.dataRate?.state === 'default',
                  'text-gray-700': !rowData.dataRate?.state
                })}
              >
                {info.getValue() ? parseFloat(info.getValue()).toFixed(1) : '-'}%
              </div>

              <div className="material-icons-outlined text-primary-300">bar_chart</div>
            </div>
          );
        }
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: 'Device status',
        cell: (info: any) => {
          return <span className="capitalize text-gray-300">{info.getValue() || '-'}</span>;
        }
      },
      {
        id: 'action',
        accessorKey: 'action',
        header: 'Action',
        cell: (info: any) => {
          const rowData = info.row.original;
          return (
            <div className="w-full flex justify-center items-center">
              <DropdownCustom<ActionsTypes>
                onClick={(type) => handleAction({ type, data: rowData })}
                controlPlaceholder="Action"
                buttons={[
                  { name: 'Edit device', id: 'edit' },
                  { name: 'Delete device', id: 'delete' },
                  {
                    name: rowData.status === 'active' ? 'Mark inactive' : 'Mark active',
                    id: 'change-status'
                  }
                ]}
              />
            </div>
          );
        }
      }
    ],
    []
  );

  return (
    <div className="rounded shadow-md bg-white dark:bg-container-700 text-gray-400 dark:text-gray-400 text-sm font-medium border-b border-b-gray-300 dark:border-b-gray-900 w-full overflow-x-auto relative">
      <TanstackTable
        columOrderStorageKey={Filters.deviceSetupColumnOrder}
        data={formattedData}
        columns={UserTableColumns}
        thClassName="bg-container-700 justify-center relative px-6 py-3"
      />
    </div>
  );
}
